import { SwiperSlide } from "swiper/swiper-react";
import { FreeMode, Navigation, Pagination } from "swiper/modules";
import React, { useEffect, useRef, useState } from "react";
import {
    ArrowIconButton,
    GroupsSwiper,
    SwiperContainer,
} from "./PreviewCardSwiper.styles";
import { VideosPreviewType } from "@infrastructure/Services/VideosPreviewService";
import { PreviewSlider } from "./PreviewSlider";
import { ChevronLeftIcon } from "@icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@icons/ChevronRightIcon";
import { isDeviceOutputDesktop } from "../../../../Domain/DeviceOutputState";
import { SkeletonsListCards } from "./SkeletonVideoPreview";
import { PostDetailsActionType } from "../../Modals/PostDetailsModal/PostDetailsModalStore";

const useSwiperRef = <T extends HTMLElement>(): [T | null, React.Ref<T>] => {
    const [wrapper, setWrapper] = useState<T | null>(null);
    const ref = useRef<T>(null);

    useEffect(() => {
        if (ref.current) {
            setWrapper(ref.current);
        }
    }, []);

    return [wrapper, ref];
};

export function VideosPreviewSwiper({
    isLoading,
    onPostCardClick,
    previews,
}: {
    isLoading: boolean;
    onPostCardClick: (postId: string, action?: PostDetailsActionType) => void;
    previews: VideosPreviewType[];
}) {
    if (isLoading) {
        return <SkeletonsListCards />;
    }
    return <ListCards previews={previews} onPostCardClick={onPostCardClick} />;
}

function ListCards({
    previews,
    onPostCardClick,
}: {
    onPostCardClick: (postId: string, action?: PostDetailsActionType) => void;
    previews: VideosPreviewType[];
}) {
    const [nextEl, nextElRef] = useSwiperRef<HTMLButtonElement>();
    const [prevEl, prevElRef] = useSwiperRef<HTMLButtonElement>();

    const isDesktop = isDeviceOutputDesktop();
    let navigation = {};
    if (isDesktop) {
        navigation = {
            prevEl,
            nextEl,
            disabledClass: "hidden",
        };
    }

    return (
        <SwiperContainer>
            <GroupsSwiper
                modules={[Navigation, FreeMode, Pagination]}
                loop={true}
                pagination={{
                    clickable: false,
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                }}
                spaceBetween={"8px"}
                watchOverflow={true}
                freeMode={true}
                breakpointsBase={"container"}
                breakpoints={{
                    0: {
                        slidesPerView: Math.min(previews.length, 1.6),
                    },
                    358: {
                        slidesPerView: Math.min(previews.length, 1.6),
                    },
                    608: {
                        slidesPerView: 3,
                    },
                    992: {
                        slidesPerView: 4,
                    },
                }}
                direction={"horizontal"}
                navigation={navigation}
            >
                {previews.map((group, key) => (
                    <SwiperSlide key={key}>
                        <PreviewSlider
                            video={group}
                            onPostCardClick={onPostCardClick}
                        />
                    </SwiperSlide>
                ))}

                {isDesktop && (
                    <>
                        <div ref={prevElRef} className="swiper-button-prev">
                            <ArrowIconButton>
                                <ChevronLeftIcon />
                            </ArrowIconButton>
                        </div>
                        <div ref={nextElRef} className="swiper-button-next">
                            <ArrowIconButton>
                                <ChevronRightIcon />
                            </ArrowIconButton>
                        </div>
                    </>
                )}
            </GroupsSwiper>
        </SwiperContainer>
    );
}
